import Vue from "vue"
import { fetchUserProfileAvatars } from "@/api/pgResources"
import {
  fetchSubscriptionCancelQuestionnaire,
  updateSubscriptions,
} from "@/api/user"
import { CANCEL_MEMBERSHIP_STEP } from "@/constants/setting"

const state = () => ({
  pageTitle: "",
  banner: "",
  avatars: [],
  cancelMembershipStep: "",
  subscriptionCancellationQuestionnaire: undefined,
  subscriptionCancellationUserAnswers: [],
  subscriptionError: "",
})

const getters = {
  cancelMembershipStep: state => state.cancelMembershipStep,
  subscriptionCancelQuestions: state => state.subscriptionCancellationQuestionnaire?.questions ?? [],
  subscriptionCancellationUserAnswers: state => state.subscriptionCancellationUserAnswers,
  subscriptionError: state => state.subscriptionError,
}

const mutations = {
  SET_PAGE_TITLE: (state, pageTitle) => {
    Vue.set(state, "pageTitle", pageTitle)
  },
  SET_BANNER: (state, banner) => {
    Vue.set(state, "banner", banner)
  },
  SET_USER_PROFILE_AVATARS: (state, avatars) => {
    Vue.set(state, "avatars", avatars)
  },
  setCancelMembershipStep: (state, step) => (state.cancelMembershipStep = step),
  SET_SUBSCRIPTION_CANCELLATION_QUESTIONNAIRE: (state, questionnaire) => (state.subscriptionCancellationQuestionnaire = questionnaire),
  SET_SUBSCRIPTION_CANCELLATION_USER_ANSWERS: (state, userAnswers) =>
    (state.subscriptionCancellationUserAnswers = userAnswers),
  UPDATE_SUBSCRIPTION_CANCELLATION_USER_ANSWER: (state, userAnswer) => {
    const answerIndex = state.subscriptionCancellationUserAnswers.findIndex(
      answer => answer.question_id === userAnswer.question_id
    )
    if (answerIndex === -1) {
      return
    }

    Vue.set(state.subscriptionCancellationUserAnswers, answerIndex, userAnswer)
  },
  setSubscriptionError: (state, error) => (state.subscriptionError = error),
}

const actions = {
  setPageTitle ({ commit }, pageTitle) {
    commit("SET_PAGE_TITLE", pageTitle)
  },
  setBanner ({ commit }, banner) {
    commit("SET_BANNER", banner)
  },
  async loadUserProfileAvatars ({ commit }) {
    const avatars = await fetchUserProfileAvatars()
    commit("SET_USER_PROFILE_AVATARS", avatars)
  },
  async fetchSubscriptionCancelQuestionnaire ({ commit, state }) {
    if (state.subscriptionCancellationQuestionnaire !== undefined) {
      return
    }

    const data = await fetchSubscriptionCancelQuestionnaire()
    const { questionnaire }  = data
    commit("SET_SUBSCRIPTION_CANCELLATION_QUESTIONNAIRE", questionnaire)

    // Pre-format user-submitted answer payload
    const questions = questionnaire?.questions ?? []
    const answers = []
    for (let question of questions) {
      const answer = {
        question_id: question.id,
        selected_options: [],
      }
      answers.push(answer)
    }
    commit("SET_SUBSCRIPTION_CANCELLATION_USER_ANSWERS", answers)
  },
  async updateSubscriptionCancelAnswer (
    { commit, state },
    { option, question }
  ) {
    const userAnswers = state.subscriptionCancellationUserAnswers
    const answer = userAnswers.find(
      answer => answer.question_id === question.id
    )
    if (!answer) {
      return
    }

    const selectedIndex = answer.selected_options.findIndex(
      selectedOption => selectedOption.option_id === option.id
    )

    // Handle state of option selections
    if (selectedIndex >= 0) {
      // Toggle selection if currently selected
      answer.selected_options.splice(selectedIndex, 1)
    } else if (
      question.max_selected_options &&
      question.max_selected_options <= answer.selected_options.length
    ) {
      // Deselect previously selected options if the maximum amount for the question is reached
      answer.selected_options.splice(0, 1)
      answer.selected_options.push({
        option_id: option.id,
      })
    } else {
      // Select the option
      answer.selected_options.push({
        option_id: option.id,
      })
    }

    commit("UPDATE_SUBSCRIPTION_CANCELLATION_USER_ANSWER", answer)
  },
  updateSubscriptionCancelAnswerCustomInput (
    { state, commit },
    { option, question, customInput }
  ) {
    const userAnswers = state.subscriptionCancellationUserAnswers
    const answer = userAnswers.find(
      answer => answer.question_id === question.id
    )
    if (!answer) {
      return
    }

    const selectedOption = answer.selected_options.find(
      selectedOption => selectedOption.option_id === option.id
    )
    if (!selectedOption) {
      return
    }

    selectedOption.custom = customInput

    commit("UPDATE_SUBSCRIPTION_CANCELLATION_USER_ANSWER", answer)
  },
  async updateSubscriptions (
    { dispatch, state, commit },
    { paymentType, subscriptionId, action }
  ) {
    commit("setSubscriptionError", "")

    const questionnaireAnswers = state.subscriptionCancellationQuestionnaire?.id
      ? {
        questionnaire_id: state.subscriptionCancellationQuestionnaire.id,
        answers: state.subscriptionCancellationUserAnswers,
      }
      : null
    const data = await updateSubscriptions(
      paymentType,
      subscriptionId,
      action,
      questionnaireAnswers
    )
    if (data.status === "fail") {
      commit("setSubscriptionError", data.error)
      if (data.error_type === "questionnaire") {
        commit("setCancelMembershipStep", CANCEL_MEMBERSHIP_STEP.QUESTIONNAIRE)
      } else if (data.error_type === "subscription") {
        commit("setCancelMembershipStep", CANCEL_MEMBERSHIP_STEP.CONFIRM)
      }
    } else {
      await dispatch("User/Subscriptions/loadUserSubscriptions", { forceRefresh: true }, { root: true })
      commit("setCancelMembershipStep", "")
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/settings.js